import { useState, useEffect, useCallback } from 'react';
import { Plus, Trash2, ToggleLeft, ToggleRight, Clock, Cpu, ChevronDown, ChevronUp } from 'lucide-react';

// ─── Types ────────────────────────────────────────────────────
interface Schedule {
  id: number;
  control_key: string;
  name: string;
  schedule_type: 'daily' | 'weekly' | 'custom';
  time_on: string | null;
  time_off: string | null;
  days_of_week: number[];
  is_active: number;
}

interface Condition {
  sensor_key: string;
  operator: 'gt' | 'lt' | 'gte' | 'lte' | 'eq';
  value: number;
}

interface Action {
  control_key: string;
  value: number;
}

interface AutoRule {
  id: number;
  name: string;
  trigger_type: string;
  conditions: Condition[];
  actions: Action[];
  is_active: number;
  last_triggered_at: string | null;
  trigger_count: number;
}

// ─── Constants ────────────────────────────────────────────────
const DAY_LABELS = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'];

const CONTROL_OPTIONS_L1 = [
  { value: 'fan_L1_1', label: 'Fan 1' },
  { value: 'fan_L1_2', label: 'Fan 2' },
  { value: 'pump_sL1', label: 'Pump' },
  { value: 'valve_fL1_1', label: 'Valve_f Zone 1' },
  { value: 'valve_fL1_2', label: 'Valve_f Zone 2' },
  { value: 'valve_fL1_3', label: 'Valve_f Zone 3' },
  { value: 'valve_fL1_4', label: 'Valve_f Zone 4' },
  { value: 'valve_dL1_1_1', label: 'Drip Z1P1' },
  { value: 'valve_dL1_1_2', label: 'Drip Z1P2' },
  { value: 'valve_dL1_2_1', label: 'Drip Z2P1' },
  { value: 'valve_dL1_2_2', label: 'Drip Z2P2' },
  { value: 'valve_dL1_3_1', label: 'Drip Z3P1' },
  { value: 'valve_dL1_3_2', label: 'Drip Z3P2' },
  { value: 'valve_dL1_4_1', label: 'Drip Z4P1' },
  { value: 'valve_dL1_4_2', label: 'Drip Z4P2' },
];

const CONTROL_OPTIONS_L2 = [
  { value: 'fan_L2_1', label: 'Fan 1' },
  { value: 'fan_L2_2', label: 'Fan 2' },
  { value: 'pump_sL2', label: 'Pump' },
  { value: 'valve_fL2_1', label: 'Valve_f Zone 1' },
  { value: 'valve_fL2_2', label: 'Valve_f Zone 2' },
  { value: 'valve_fL2_3', label: 'Valve_f Zone 3' },
  { value: 'valve_fL2_4', label: 'Valve_f Zone 4' },
  { value: 'light_L2_1', label: 'ไฟ Zone 1' },
  { value: 'light_L2_2', label: 'ไฟ Zone 2' },
  { value: 'light_L2_3', label: 'ไฟ Zone 3' },
  { value: 'light_L2_4', label: 'ไฟ Zone 4' },
  { value: 'valve_dL2_1_1', label: 'Drip Z1P1' },
  { value: 'valve_dL2_1_2', label: 'Drip Z1P2' },
  { value: 'valve_dL2_2_1', label: 'Drip Z2P1' },
  { value: 'valve_dL2_2_2', label: 'Drip Z2P2' },
  { value: 'valve_dL2_3_1', label: 'Drip Z3P1' },
  { value: 'valve_dL2_3_2', label: 'Drip Z3P2' },
  { value: 'valve_dL2_4_1', label: 'Drip Z4P1' },
  { value: 'valve_dL2_4_2', label: 'Drip Z4P2' },
];

const SENSOR_OPTIONS_L1 = [
  { value: 'TempAir_L1', label: 'อุณหภูมิอากาศ (°C)' },
  { value: 'humidity_L1', label: 'ความชื้นอากาศ (%)' },
  { value: 'co2_L1', label: 'CO₂ (ppm)' },
  { value: 'lux_L1', label: 'แสง (lux)' },
];

const SENSOR_OPTIONS_L2 = [
  { value: 'TempAir_L2', label: 'อุณหภูมิอากาศ (°C)' },
  { value: 'humidity_L2', label: 'ความชื้นอากาศ (%)' },
  { value: 'co2_L2', label: 'CO₂ (ppm)' },
  { value: 'lux_L2', label: 'แสง (lux)' },
];

const OPERATOR_OPTIONS = [
  { value: 'gt', label: '> มากกว่า' },
  { value: 'gte', label: '>= มากกว่าหรือเท่ากับ' },
  { value: 'lt', label: '< น้อยกว่า' },
  { value: 'lte', label: '<= น้อยกว่าหรือเท่ากับ' },
  { value: 'eq', label: '= เท่ากับ' },
];

// ─── API helpers ──────────────────────────────────────────────
const API = '/api/admin';

async function apiFetch(url: string, options?: RequestInit) {
  const res = await fetch(url, { credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...options });
  return res.json();
}

// ─── Schedule Form Modal ──────────────────────────────────────
function ScheduleModal({ layer, projectKey, ghKey, onClose, onSaved }: {
  layer: 'L1' | 'L2'; projectKey: string; ghKey: string;
  onClose: () => void; onSaved: () => void;
}) {
  const controlOptions = layer === 'L1' ? CONTROL_OPTIONS_L1 : CONTROL_OPTIONS_L2;
  const [name, setName] = useState('');
  const [controlKey, setControlKey] = useState(controlOptions[0].value);
  const [timeOn, setTimeOn] = useState('06:00');
  const [timeOff, setTimeOff] = useState('18:00');
  const [days, setDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);
  const [saving, setSaving] = useState(false);

  const toggleDay = (d: number) => setDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());

  const save = async () => {
    if (!name.trim()) return alert('กรุณาใส่ชื่อ');
    setSaving(true);
    await apiFetch(`${API}/schedules/${projectKey}/${ghKey}`, {
      method: 'POST',
      body: JSON.stringify({ name, control_key: controlKey, schedule_type: days.length === 7 ? 'daily' : 'weekly', time_on: timeOn || null, time_off: timeOff || null, days_of_week: days }),
    });
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">เพิ่ม Timer Schedule</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">ชื่อ</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="เช่น เปิดปั๊มเช้า"
              className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">อุปกรณ์</label>
            <select value={controlKey} onChange={e => setControlKey(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              {controlOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">เวลาเปิด</label>
              <input type="time" value={timeOn} onChange={e => setTimeOn(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">เวลาปิด</label>
              <input type="time" value={timeOff} onChange={e => setTimeOff(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-2 block">วันที่ทำงาน</label>
            <div className="flex gap-1.5">
              {DAY_LABELS.map((label, i) => (
                <button key={i} onClick={() => toggleDay(i)}
                  className={`flex-1 py-1.5 rounded-lg text-xs font-semibold border transition-all
                    ${days.includes(i) ? 'bg-primary text-white border-primary' : 'bg-white dark:bg-gray-700 border-gray-200 dark:border-gray-600 text-gray-500'}`}>
                  {label}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300">ยกเลิก</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sensor Rule Form Modal ────────────────────────────────────
function SensorRuleModal({ layer, projectKey, ghKey, onClose, onSaved }: {
  layer: 'L1' | 'L2'; projectKey: string; ghKey: string;
  onClose: () => void; onSaved: () => void;
}) {
  const controlOptions = layer === 'L1' ? CONTROL_OPTIONS_L1 : CONTROL_OPTIONS_L2;
  const sensorOptions = layer === 'L1' ? SENSOR_OPTIONS_L1 : SENSOR_OPTIONS_L2;
  const [name, setName] = useState('');
  const [sensorKey, setSensorKey] = useState(sensorOptions[0].value);
  const [operator, setOperator] = useState<'gt'|'lt'|'gte'|'lte'|'eq'>('gt');
  const [threshold, setThreshold] = useState('35');
  const [actionControl, setActionControl] = useState(controlOptions[0].value);
  const [actionValue, setActionValue] = useState<1|0>(1);
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) return alert('กรุณาใส่ชื่อ');
    setSaving(true);
    await apiFetch(`${API}/automation/${projectKey}/${ghKey}`, {
      method: 'POST',
      body: JSON.stringify({
        name, trigger_type: 'sensor',
        trigger_config: {},
        conditions: [{ sensor_key: sensorKey, operator, value: parseFloat(threshold) }],
        actions: [{ control_key: actionControl, value: actionValue }],
      }),
    });
    setSaving(false);
    onSaved();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 px-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-5 space-y-4">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">เพิ่ม Sensor Rule</h3>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">ชื่อ</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="เช่น เปิดพัดลมเมื่อร้อน"
              className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">เงื่อนไข (ถ้า...)</p>
          <div>
            <label className="text-xs font-semibold text-gray-500 mb-1 block">เซนเซอร์</label>
            <select value={sensorKey} onChange={e => setSensorKey(e.target.value)}
              className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
              {sensorOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">เงื่อนไข</label>
              <select value={operator} onChange={e => setOperator(e.target.value as any)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                {OPERATOR_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="w-24">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">ค่า</label>
              <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100" />
            </div>
          </div>
          <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">การกระทำ (แล้ว...)</p>
          <div className="flex gap-3">
            <div className="flex-1">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">อุปกรณ์</label>
              <select value={actionControl} onChange={e => setActionControl(e.target.value)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                {controlOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div className="w-24">
              <label className="text-xs font-semibold text-gray-500 mb-1 block">สั่ง</label>
              <select value={actionValue} onChange={e => setActionValue(Number(e.target.value) as 1|0)}
                className="w-full border border-gray-200 dark:border-gray-600 rounded-lg px-3 py-2 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100">
                <option value={1}>เปิด</option>
                <option value={0}>ปิด</option>
              </select>
            </div>
          </div>
        </div>
        <div className="flex gap-2 pt-1">
          <button onClick={onClose} className="flex-1 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-sm font-semibold text-gray-600 dark:text-gray-300">ยกเลิก</button>
          <button onClick={save} disabled={saving}
            className="flex-1 py-2.5 rounded-xl bg-primary text-white text-sm font-semibold disabled:opacity-50">
            {saving ? 'กำลังบันทึก...' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Schedule Card ─────────────────────────────────────────────
function ScheduleCard({ s, projectKey, ghKey, onRefresh }: {
  s: Schedule; projectKey: string; ghKey: string; onRefresh: () => void;
}) {
  const controlOptions = [...CONTROL_OPTIONS_L1, ...CONTROL_OPTIONS_L2];
  const label = controlOptions.find(o => o.value === s.control_key)?.label ?? s.control_key;

  const toggle = async () => {
    await apiFetch(`${API}/schedules/${projectKey}/${ghKey}/${s.id}/toggle`, { method: 'PUT' });
    onRefresh();
  };

  const del = async () => {
    if (!confirm(`ลบ "${s.name}"?`)) return;
    await apiFetch(`${API}/schedules/${projectKey}/${ghKey}/${s.id}`, { method: 'DELETE' });
    onRefresh();
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 border shadow-sm transition-all
      ${s.is_active ? 'border-blue-200 dark:border-blue-800' : 'border-gray-100 dark:border-gray-700 opacity-60'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{s.name}</p>
          <p className="text-xs text-gray-500 mt-0.5">{label}</p>
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-600 dark:text-gray-400">
            {s.time_on && <span className="flex items-center gap-1"><span className="text-green-500 font-bold">ON</span> {s.time_on}</span>}
            {s.time_off && <span className="flex items-center gap-1"><span className="text-red-500 font-bold">OFF</span> {s.time_off}</span>}
          </div>
          {s.days_of_week.length < 7 && (
            <div className="flex gap-1 mt-2">
              {DAY_LABELS.map((d, i) => (
                <span key={i} className={`text-[10px] px-1.5 py-0.5 rounded font-semibold
                  ${s.days_of_week.includes(i) ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'bg-gray-100 text-gray-300 dark:bg-gray-700'}`}>
                  {d}
                </span>
              ))}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            {s.is_active ? <ToggleRight className="w-5 h-5 text-blue-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
          </button>
          <button onClick={del} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Sensor Rule Card ─────────────────────────────────────────
function SensorRuleCard({ r, projectKey, ghKey, onRefresh }: {
  r: AutoRule; projectKey: string; ghKey: string; onRefresh: () => void;
}) {
  const allControls = [...CONTROL_OPTIONS_L1, ...CONTROL_OPTIONS_L2];
  const allSensors = [...SENSOR_OPTIONS_L1, ...SENSOR_OPTIONS_L2];

  const toggle = async () => {
    await apiFetch(`${API}/automation/${projectKey}/${ghKey}/${r.id}/toggle`, { method: 'PUT' });
    onRefresh();
  };

  const del = async () => {
    if (!confirm(`ลบ "${r.name}"?`)) return;
    await apiFetch(`${API}/automation/${projectKey}/${ghKey}/${r.id}`, { method: 'DELETE' });
    onRefresh();
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-xl p-4 border shadow-sm transition-all
      ${r.is_active ? 'border-green-200 dark:border-green-800' : 'border-gray-100 dark:border-gray-700 opacity-60'}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-gray-900 dark:text-gray-100 truncate">{r.name}</p>
          {r.conditions.map((c, i) => {
            const sLabel = allSensors.find(s => s.value === c.sensor_key)?.label ?? c.sensor_key;
            const opLabel = OPERATOR_OPTIONS.find(o => o.value === c.operator)?.label ?? c.operator;
            return (
              <p key={i} className="text-xs text-gray-500 mt-1">
                ถ้า <span className="font-medium text-gray-700 dark:text-gray-300">{sLabel}</span> {opLabel} <span className="font-bold text-primary">{c.value}</span>
              </p>
            );
          })}
          {r.actions.map((a, i) => {
            const cLabel = allControls.find(c => c.value === a.control_key)?.label ?? a.control_key;
            return (
              <p key={i} className="text-xs text-gray-500 mt-0.5">
                → <span className="font-medium text-gray-700 dark:text-gray-300">{cLabel}</span> <span className={`font-bold ${a.value ? 'text-green-500' : 'text-red-500'}`}>{a.value ? 'เปิด' : 'ปิด'}</span>
              </p>
            );
          })}
          {r.trigger_count > 0 && (
            <p className="text-[10px] text-gray-400 mt-1.5">ทำงานแล้ว {r.trigger_count} ครั้ง {r.last_triggered_at ? `· ล่าสุด ${new Date(r.last_triggered_at).toLocaleString('th-TH')}` : ''}</p>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button onClick={toggle} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
            {r.is_active ? <ToggleRight className="w-5 h-5 text-green-500" /> : <ToggleLeft className="w-5 h-5 text-gray-400" />}
          </button>
          <button onClick={del} className="p-1.5 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors">
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main AutomationSection ────────────────────────────────────
export function AutomationSection({ layer, project, gh }: {
  layer: 'L1' | 'L2'; project: string; gh: string;
}) {
  const projectKey = project;
  const ghKey = gh;

  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [rules, setRules] = useState<AutoRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [timerOpen, setTimerOpen] = useState(true);
  const [sensorOpen, setSensorOpen] = useState(true);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, rRes] = await Promise.all([
        apiFetch(`${API}/schedules/${projectKey}/${ghKey}`),
        apiFetch(`${API}/automation/${projectKey}/${ghKey}`),
      ]);
      setSchedules(sRes.data?.schedules ?? []);
      setRules((rRes.data?.rules ?? []).filter((r: AutoRule) => r.trigger_type === 'sensor'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [projectKey, ghKey]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  return (
    <section>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3 flex items-center gap-2">
        <span className="w-1.5 h-5 bg-purple-500 rounded-full"></span>ระบบอัตโนมัติ
      </h2>

      {loading ? (
        <div className="text-center py-8 text-gray-400 text-sm">กำลังโหลด...</div>
      ) : (
        <div className="space-y-4">

          {/* ── Timer Section ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <button onClick={() => setTimerOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 transition-colors">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-500" />
                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Timer Schedule ({schedules.length})</span>
              </div>
              {timerOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {timerOpen && (
              <div className="p-3 space-y-2 bg-white dark:bg-gray-900">
                {schedules.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-4">ยังไม่มี Schedule</p>
                )}
                {schedules.map(s => (
                  <ScheduleCard key={s.id} s={s} projectKey={projectKey} ghKey={ghKey} onRefresh={fetchAll} />
                ))}
                <button onClick={() => setShowScheduleModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-blue-300 dark:border-blue-700 text-blue-500 text-sm font-semibold hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors mt-1">
                  <Plus className="w-4 h-4" /> เพิ่ม Schedule
                </button>
              </div>
            )}
          </div>

          {/* ── Sensor Section ── */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <button onClick={() => setSensorOpen(o => !o)}
              className="w-full flex items-center justify-between px-4 py-3 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 transition-colors">
              <div className="flex items-center gap-2">
                <Cpu className="w-4 h-4 text-green-500" />
                <span className="font-semibold text-gray-800 dark:text-gray-200 text-sm">Sensor Rule ({rules.length})</span>
              </div>
              {sensorOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </button>
            {sensorOpen && (
              <div className="p-3 space-y-2 bg-white dark:bg-gray-900">
                {rules.length === 0 && (
                  <p className="text-center text-sm text-gray-400 py-4">ยังไม่มี Sensor Rule</p>
                )}
                {rules.map(r => (
                  <SensorRuleCard key={r.id} r={r} projectKey={projectKey} ghKey={ghKey} onRefresh={fetchAll} />
                ))}
                <button onClick={() => setShowRuleModal(true)}
                  className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border-2 border-dashed border-green-300 dark:border-green-700 text-green-500 text-sm font-semibold hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors mt-1">
                  <Plus className="w-4 h-4" /> เพิ่ม Sensor Rule
                </button>
              </div>
            )}
          </div>

        </div>
      )}

      {showScheduleModal && (
        <ScheduleModal layer={layer} projectKey={projectKey} ghKey={ghKey}
          onClose={() => setShowScheduleModal(false)} onSaved={fetchAll} />
      )}
      {showRuleModal && (
        <SensorRuleModal layer={layer} projectKey={projectKey} ghKey={ghKey}
          onClose={() => setShowRuleModal(false)} onSaved={fetchAll} />
      )}
    </section>
  );
}
